import { TeamName } from "@69ly/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { Logo } from "../components/Logo.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useCreateTeam, useMe } from "../lib/queries.ts";

/** Reads a code from a full invite link (https://…/join/CODE) or a bare code. */
export function inviteCodeFrom(input: string): string | null {
  const trimmed = input.trim();
  const match = /(?:^|\/join\/)([A-Za-z0-9_-]{12})\/?$/.exec(trimmed);
  return match ? match[1] : null;
}

function Steps() {
  const { t } = useTranslation();
  const steps = [t("setup.stepSteam"), t("setup.stepTeam"), t("setup.stepLive")];
  return (
    <ol className="steps" aria-label={t("setup.steps")}>
      {steps.map((label, i) => (
        <li key={label} className={i === 0 ? "is-done" : i === 1 ? "is-current" : undefined} aria-current={i === 1 ? "step" : undefined}>
          <span className="step-disc">{i === 0 ? <Icon name="check" size={14} label={label} /> : i + 1}</span>
          {label}
        </li>
      ))}
    </ol>
  );
}

export function SetupPage() {
  const { t } = useTranslation();
  const me = useMe().data?.user;
  const navigate = useNavigate();
  const { select } = useCurrentTeam();
  const createTeam = useCreateTeam();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const create = (e: FormEvent) => {
    e.preventDefault();
    const parsed = TeamName.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? t("errors.invalid_input"));
      return;
    }
    setNameError(null);
    createTeam.mutate(parsed.data, {
      onSuccess: (team) => {
        select(team.id);
        navigate("/team", { replace: true });
      },
    });
  };

  const join = (e: FormEvent) => {
    e.preventDefault();
    const code = inviteCodeFrom(link);
    if (!code) {
      setLinkError(t("setup.invalidLink"));
      return;
    }
    navigate(`/join/${code}`);
  };

  return (
    <div className="page page-narrow setup">
      <div className="setup-top">
        <Logo />
        <Steps />
      </div>
      <div className="setup-welcome">
        {me && <Avatar name={me.name} url={me.avatarUrl} me size={48} />}
        <div>
          <h1 className="page-title">{t("setup.title", { name: me?.name ?? "" })}</h1>
          <p className="muted">{t("setup.intro")}</p>
        </div>
      </div>

      <div className="setup-cards">
        <form className="card card-accent" onSubmit={create} noValidate>
          <h2 className="section-title">{t("setup.createTitle")}</h2>
          <p className="muted">{t("setup.createHelp")}</p>
          <label className="field">
            <span>{t("setup.teamName")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("setup.teamNamePlaceholder")}
              maxLength={32}
              aria-invalid={Boolean(nameError) || undefined}
              aria-describedby={nameError ? "team-name-error" : undefined}
            />
          </label>
          {(nameError || createTeam.error) && (
            <p id="team-name-error" className="field-error">
              {nameError ?? errorText(t, createTeam.error)}
            </p>
          )}
          <button type="submit" className="btn btn-primary" disabled={createTeam.isPending}>
            {t("setup.create")}
          </button>
        </form>

        <form className="card" onSubmit={join} noValidate>
          <h2 className="section-title">{t("setup.joinTitle")}</h2>
          <p className="muted">{t("setup.joinHelp")}</p>
          <label className="field">
            <span>{t("setup.inviteLink")}</span>
            <input
              value={link}
              onChange={(e) => {
                setLink(e.target.value);
                setLinkError(null);
              }}
              placeholder="https://…/join/…"
              aria-invalid={Boolean(linkError) || undefined}
              aria-describedby={linkError ? "invite-error" : undefined}
            />
          </label>
          {linkError && (
            <p id="invite-error" className="field-error">
              {linkError}
            </p>
          )}
          <button type="submit" className="btn btn-secondary">
            {t("setup.join")}
          </button>
        </form>
      </div>

      <Link to="/library" className="setup-skip">
        {t("setup.solo")}
      </Link>
    </div>
  );
}
